import {Box, Tooltip, Typography} from '@mui/material';
import {useMemo, useState} from 'react';
import {
  createGradientStops,
  getMinMaxPosition,
  groupLabeledStops,
} from './utils.ts';
import {LayerLegend as Legend} from '@src/types/configurations';

export function ContinuousLegend({legend}: {legend: Legend}) {
  const {minPosition, maxPosition} = getMinMaxPosition(legend);
  const [activePos, setActivePos] = useState<number | null>(null);

  const groupedStops = useMemo(() => groupLabeledStops(legend), [legend]);

  const gradientStops = useMemo(
    () => createGradientStops(legend, minPosition, maxPosition),
    [legend, minPosition, maxPosition],
  );

  if (minPosition === undefined || maxPosition === undefined) {
    return null;
  }

  const range = maxPosition - minPosition || 1;

  const normalize = (position: number) =>
    ((position - minPosition) / range) * 100;

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        paddingInline: 2,
        paddingBlockEnd: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: 48,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            insetInlineStart: 0,
            insetInlineEnd: 0,
            insetBlockEnd: 0,
            height: 24,
            overflow: 'hidden',
            background: `linear-gradient(to right, ${gradientStops})`,
            zIndex: 2,
          }}
        />

        <Box
          sx={{
            position: 'relative',
            height: 48,
            marginInline: 0.1,
          }}
        >
          {groupedStops.map(stop => {
            const clampedPos = Math.min(
              Math.max(stop.position, minPosition),
              maxPosition,
            );
            const left = normalize(clampedPos);
            const isActive = activePos === stop.position;

            // Remove spaces between minus and number
            const normalizeLabel = (label: string) =>
              label.replace(/^-\s+/, '-').trim();

            const primaryLabel = normalizeLabel(stop.labels[0]);
            const extraLabels = stop.labels.slice(1).map(normalizeLabel);

            const tooltipTitle =
              extraLabels.length > 0
                ? `${primaryLabel} (${extraLabels.join(', ')})`
                : null;

            return (
              <Box
                key={stop.position}
                sx={{
                  position: 'absolute',
                  insetInlineStart: `${left}%`,
                  transform: 'translateX(-50%)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Tooltip
                  title={tooltipTitle}
                  arrow
                  disableInteractive
                  placement="top"
                >
                  <Box
                    onMouseEnter={() => setActivePos(stop.position)}
                    onMouseLeave={() => setActivePos(null)}
                    sx={{
                      position: 'absolute',
                      insetBlockStart: -4,
                      pointerEvents: 'auto',
                      paddingInline: 0.5,
                      borderRadius: 0.75,
                      bgcolor: isActive ? 'background.paper' : 'transparent',
                      boxShadow: isActive ? 2 : 'none',
                      transform: isActive ? 'translateY(-2px)' : 'none',
                      transition: 'all 120ms ease-out',
                      cursor: 'default',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: 'center',
                        color: isActive ? 'text.primary' : 'text.secondary',
                        fontSize: 13,
                        fontWeight: isActive ? 600 : 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {primaryLabel}
                    </Typography>
                  </Box>
                </Tooltip>

                <Box
                  onMouseEnter={() => setActivePos(stop.position)}
                  onMouseLeave={() => setActivePos(null)}
                  sx={{
                    pointerEvents: 'auto',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    sx={{
                      width: 1.5,
                      height: 31,
                      marginInline: 0.1,
                      bgcolor: isActive ? 'info.dark' : 'info.main',
                      boxShadow: isActive ? 2 : 'none',
                      transition: 'all 120ms ease-out',
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
